//frontend/src/components/Dossier/DossierDetails.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Badge, Button, Alert, Form, Row, Col, Table, Modal, ProgressBar } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
    FaFileUpload, FaCheck, FaTimes, FaEye, FaTrash, FaClock,
    FaExclamationTriangle, FaBriefcase, FaGraduationCap,
    FaChevronLeft, FaChevronRight, FaClipboardCheck, FaFileAlt,
    FaCreditCard,
} from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

// ── Constants ──────────────────────────────────────────────────────────────────
const VISA_LABELS = {
    work: 'Visa de Travail', study: "Visa d'Études", tourist: 'Visa Touristique',
    family: 'Regroupement Familial', business: "Visa d'Affaires",
};

const STATUS_CONFIG = {
    draft:     { label: 'Brouillon',   variant: 'secondary' },
    submitted: { label: 'Soumis',      variant: 'info'      },
    in_review: { label: 'En révision', variant: 'warning'   },
    approved:  { label: 'Approuvé',    variant: 'success'   },
    rejected:  { label: 'Rejeté',      variant: 'danger'    },
};

const REQUIRED_DOCS = [
    { value: 'passport',          label: 'Passeport' },
    { value: 'photo',             label: "Photo d'identité" },
    { value: 'birth_certificate', label: 'Acte de naissance' },
    { value: 'cv',                label: 'CV' },
    { value: 'diploma',           label: 'Diplôme' },
    { value: 'motivation_letter', label: 'Lettre de motivation' },
    { value: 'bank_statement',    label: 'Relevé bancaire' },
];

const OPTIONAL_DOCS = [
    { value: 'transcript',           label: 'Relevé de notes' },
    { value: 'work_contract',        label: 'Contrat de travail' },
    { value: 'job_offer',            label: "Offre d'emploi" },
    { value: 'language_certificate', label: 'Certificat de langue' },
    { value: 'marriage_certificate', label: 'Certificat de mariage' },
    { value: 'police_certificate',   label: 'Casier judiciaire' },
    { value: 'medical_certificate',  label: 'Certificat médical' },
    { value: 'other',                label: 'Autre document' },
];

const ALL_DOCS = [...REQUIRED_DOCS, ...OPTIONAL_DOCS];

const WORK_CITIES = [
    { value: 'toronto', label: 'Toronto, Ontario' }, { value: 'montreal', label: 'Montréal, Québec' },
    { value: 'vancouver', label: 'Vancouver, C.-B.' }, { value: 'calgary', label: 'Calgary, Alberta' },
    { value: 'ottawa', label: 'Ottawa, Ontario' }, { value: 'edmonton', label: 'Edmonton, Alberta' },
    { value: 'winnipeg', label: 'Winnipeg, Manitoba' }, { value: 'quebec', label: 'Québec, Québec' },
    { value: 'hamilton', label: 'Hamilton, Ontario' }, { value: 'other', label: 'Autre ville' },
];

const WORK_SECTORS = [
    { value: 'tech', label: "Technologies de l'information" }, { value: 'health', label: 'Santé' },
    { value: 'education', label: 'Éducation' }, { value: 'finance', label: 'Finance' },
    { value: 'construction', label: 'Construction' }, { value: 'hospitality', label: 'Hôtellerie/Restauration' },
    { value: 'retail', label: 'Commerce de détail' }, { value: 'manufacturing', label: 'Industrie manufacturière' },
    { value: 'agriculture', label: 'Agriculture' }, { value: 'other', label: 'Autre secteur' },
];

const STUDY_LEVELS = [
    { value: 'high_school', label: 'Lycée (High School)' }, { value: 'college', label: 'Collège (CEGEP)' },
    { value: 'bachelor', label: 'Licence / Bachelor' }, { value: 'master', label: 'Master / Maîtrise' },
    { value: 'doctorate', label: 'Doctorat (PhD)' }, { value: 'language', label: 'Cours de langue' },
];

const STUDY_CITIES = ['Toronto', 'Montréal', 'Vancouver', 'Calgary', 'Ottawa', 'Edmonton', 'Winnipeg', 'Québec', 'Hamilton', 'London', 'Autre'];

// ── Step Indicator ─────────────────────────────────────────────────────────────
const StepIndicator = ({ steps, currentStep }) => (
    <div className="d-flex justify-content-between align-items-flex-start mb-4 position-relative">
        <div className="position-absolute w-100" style={{ height: 2, backgroundColor: '#dee2e6', top: 20, zIndex: 0 }} />
        {steps.map((step, index) => {
            const Icon = step.icon;
            const done = index < currentStep;
            const active = index === currentStep;
            return (
                <div key={step.id} className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 1, flex: 1 }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                        style={{
                            width: 40, height: 40,
                            backgroundColor: done ? '#198754' : active ? '#0d6efd' : '#dee2e6',
                            color: done || active ? 'white' : '#6c757d',
                            boxShadow: active ? '0 0 0 4px rgba(13,110,253,0.2)' : 'none',
                            transition: 'all 0.3s',
                        }}>
                        {done ? <FaCheck size={13} /> : <Icon size={13} />}
                    </div>
                    <small className="mt-1 text-center d-none d-md-block"
                        style={{ color: active ? '#0d6efd' : done ? '#198754' : '#adb5bd', fontWeight: active ? 600 : 400, fontSize: '0.72rem' }}>
                        {step.title}
                    </small>
                </div>
            );
        })}
    </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const DossierDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [dossier, setDossier]     = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [currentStep, setCurrentStep] = useState(0);
    const [saving, setSaving]       = useState(false);

    // Preferences
    const [workPrefs, setWorkPrefs] = useState({
        preferred_city: '', preferred_sector: '', job_title_sought: '',
        years_experience: '', minimum_salary: '', key_skills: '',
        certifications: '', available_from: '', willing_to_relocate: true, additional_notes: '',
    });
    const [studyPrefs, setStudyPrefs] = useState({
        study_level: '', field_of_study: '', preferred_university: '',
        preferred_city: '', start_date: '', duration_months: '',
        has_acceptance_letter: false, funding_source: '',
    });
    const [prefsExist, setPrefsExist] = useState(false);

    // Document upload
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [docType, setDocType]       = useState('');
    const [uploading, setUploading]   = useState(false);

    // Payment
    const [paymentReceipt, setPaymentReceipt]       = useState(null);
    const [uploadingPayment, setUploadingPayment]   = useState(false);

    // ── Data loading ───────────────────────────────────────────────────────────
    const loadDocuments = useCallback(async () => {
        try {
            const res = await api.get(`/dossiers/${id}/documents/`);
            setDocuments(Array.isArray(res.data) ? res.data : []);
        } catch {}
    }, [id]);

    const loadPreferences = useCallback(async (visaType) => {
        if (visaType !== 'work' && visaType !== 'study') return;
        try {
            const endpoint = visaType === 'work'
                ? `/dossiers/${id}/work-preferences/`
                : `/dossiers/${id}/study-preferences/`;
            const res = await api.get(endpoint);
            if (visaType === 'work') setWorkPrefs(res.data.preferences);
            else setStudyPrefs(res.data.preferences);
            setPrefsExist(true);
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error loading preferences:', err);
        }
    }, [id]);

    useEffect(() => {
        const init = async () => {
            try {
                const res = await api.get(`/dossiers/${id}/`);
                setDossier(res.data);
                await Promise.all([
                    loadDocuments(),
                    loadPreferences(res.data.visa_type),
                ]);
            } catch {
                toast.error('Erreur lors du chargement du dossier');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [id, loadDocuments, loadPreferences]);

    // ── Steps definition ───────────────────────────────────────────────────────
    const STEPS = React.useMemo(() => {
        if (!dossier) return [];
        const hasPrefs = dossier.visa_type === 'work' || dossier.visa_type === 'study';
        const prefsIcon = dossier.visa_type === 'work' ? FaBriefcase : FaGraduationCap;
        const base = [
            { id: 'documents', title: 'Documents',  icon: FaFileAlt },
            { id: 'payment',   title: 'Paiement',   icon: FaCreditCard },
            { id: 'review',    title: 'Soumission',  icon: FaClipboardCheck },
        ];
        return hasPrefs
            ? [{ id: 'preferences', title: 'Préférences', icon: prefsIcon }, ...base]
            : base;
    }, [dossier]);

    // ── Computed ───────────────────────────────────────────────────────────────
    const uploadedTypes   = documents.map(d => d.doc_type);
    const missingRequired = REQUIRED_DOCS.filter(d => !uploadedTypes.includes(d.value));
    const docCompletion   = Math.round(((REQUIRED_DOCS.length - missingRequired.length) / REQUIRED_DOCS.length) * 100);
    const hasPrefsStep    = STEPS[0]?.id === 'preferences';

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleWorkChange  = (e) => {
        const { name, value, type, checked } = e.target;
        setWorkPrefs(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    const handleStudyChange = (e) => {
        const { name, value, type, checked } = e.target;
        setStudyPrefs(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const savePreferences = async () => {
        const isWork  = dossier.visa_type === 'work';
        const url     = isWork ? `/dossiers/${id}/work-preferences/` : `/dossiers/${id}/study-preferences/`;
        const data    = isWork ? workPrefs : studyPrefs;
        const method  = prefsExist ? 'patch' : 'post';
        await api[method](url, data);
        setPrefsExist(true);
    };

    const handleNext = async () => {
        setSaving(true);
        try {
            if (STEPS[currentStep]?.id === 'preferences') {
                await savePreferences();
                toast.success('✅ Préférences sauvegardées');
            }
            setCurrentStep(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {
            toast.error('❌ Erreur lors de la sauvegarde des préférences');
        } finally {
            setSaving(false);
        }
    };

    const handlePrev = () => {
        setCurrentStep(prev => prev - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file?.size > 10 * 1024 * 1024) { toast.error('Max 10 MB'); return; }
        setUploadFile(file);
    };

    const handleFileUpload = async () => {
        if (!uploadFile || !docType) { toast.error('Sélectionnez un fichier et un type'); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('doc_type', docType);
        try {
            await api.post(`/dossiers/${id}/documents/`, fd);
            toast.success('✅ Document téléversé');
            setShowUploadModal(false);
            setUploadFile(null);
            setDocType('');
            loadDocuments();
        } catch { toast.error('❌ Erreur lors du téléversement'); }
        finally { setUploading(false); }
    };

    const handleDeleteDocument = async (docId) => {
        if (!window.confirm('Supprimer ce document ?')) return;
        try {
            await api.delete(`/documents/${docId}`);
            toast.success('Document supprimé');
            loadDocuments();
        } catch { toast.error('Erreur lors de la suppression'); }
    };

    const handlePaymentReceiptUpload = async () => {
        if (!paymentReceipt) { toast.error('Sélectionnez un fichier'); return; }
        if (paymentReceipt.size > 10 * 1024 * 1024) { toast.error('Max 10 MB'); return; }
        setUploadingPayment(true);
        const fd = new FormData();
        fd.append('payment_receipt', paymentReceipt);
        try {
            const res = await api.post(`/dossiers/${id}/upload-payment-receipt`, fd);
            toast.success('✅ Reçu de paiement téléversé');
            setPaymentReceipt(null);
            setDossier(res.data);
        } catch { toast.error('❌ Erreur lors du téléversement du reçu'); }
        finally { setUploadingPayment(false); }
    };

    const handleSubmitDossier = async () => {
        if (missingRequired.length > 0) {
            toast.error(`Documents manquants : ${missingRequired.map(d => d.label).join(', ')}`);
            return;
        }
        if (!window.confirm('Êtes-vous sûr de vouloir soumettre votre dossier ? Vous ne pourrez plus le modifier.')) return;
        try {
            await api.post(`/dossiers/${id}/submit/`);
            toast.success('✅ Dossier soumis avec succès !');
            const res = await api.get(`/dossiers/${id}/`);
            setDossier(res.data);
        } catch (err) {
            toast.error(`❌ ${err.response?.data?.error || 'Erreur lors de la soumission'}`);
        }
    };

    const handleDeleteDossier = async () => {
        if (!window.confirm('⚠️ Supprimer ce dossier ? Cette action est irréversible.')) return;
        try {
            await api.delete(`/dossiers/${id}/`);
            toast.success('Dossier supprimé');
            navigate('/mes-dossiers');
        } catch (err) { toast.error(err.response?.data?.error || 'Erreur lors de la suppression'); }
    };

    // ── Step renderers ─────────────────────────────────────────────────────────
    const renderPreferencesStep = () => {
        const ro = dossier.status !== 'draft';
        if (dossier.visa_type === 'work') return (
            <>
                <h5 className="mb-4">Vos préférences pour le <strong>Visa de Travail</strong></h5>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ville préférée *</Form.Label>
                            <Form.Select name="preferred_city" value={workPrefs.preferred_city} onChange={handleWorkChange} disabled={ro} required>
                                <option value="">Sélectionner</option>
                                {WORK_CITIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Secteur d'activité *</Form.Label>
                            <Form.Select name="preferred_sector" value={workPrefs.preferred_sector} onChange={handleWorkChange} disabled={ro} required>
                                <option value="">Sélectionner</option>
                                {WORK_SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Label>Intitulé du poste recherché *</Form.Label>
                    <Form.Control name="job_title_sought" value={workPrefs.job_title_sought} onChange={handleWorkChange} disabled={ro} placeholder="Ex: Développeur Web, Infirmier(ère), Chef de projet..." required />
                </Form.Group>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Années d'expérience *</Form.Label>
                            <Form.Control type="number" name="years_experience" value={workPrefs.years_experience} onChange={handleWorkChange} disabled={ro} min="0" max="50" required />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Salaire minimum souhaité (CAD/an) *</Form.Label>
                            <Form.Control type="number" name="minimum_salary" value={workPrefs.minimum_salary} onChange={handleWorkChange} disabled={ro} min="0" placeholder="Ex: 50000" required />
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Label>Compétences clés *</Form.Label>
                    <Form.Control as="textarea" rows={3} name="key_skills" value={workPrefs.key_skills} onChange={handleWorkChange} disabled={ro} placeholder="Ex: JavaScript, React, SQL, Gestion de projet..." required />
                </Form.Group>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Certifications / Diplômes</Form.Label>
                            <Form.Control as="textarea" rows={2} name="certifications" value={workPrefs.certifications} onChange={handleWorkChange} disabled={ro} placeholder="Ex: AWS, PMP, CCNA..." />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Disponible à partir du</Form.Label>
                            <Form.Control type="date" name="available_from" value={workPrefs.available_from} onChange={handleWorkChange} disabled={ro} />
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Check type="checkbox" name="willing_to_relocate" checked={workPrefs.willing_to_relocate} onChange={handleWorkChange} disabled={ro}
                        label="Je suis ouvert(e) à d'autres villes si l'opportunité est intéressante" />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Notes supplémentaires</Form.Label>
                    <Form.Control as="textarea" rows={2} name="additional_notes" value={workPrefs.additional_notes} onChange={handleWorkChange} disabled={ro} />
                </Form.Group>
            </>
        );
        // Study
        return (
            <>
                <h5 className="mb-4">Vos préférences pour le <strong>Visa d'Études</strong></h5>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Niveau d'études *</Form.Label>
                            <Form.Select name="study_level" value={studyPrefs.study_level} onChange={handleStudyChange} disabled={ro} required>
                                <option value="">Sélectionner</option>
                                {STUDY_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Domaine d'études *</Form.Label>
                            <Form.Control name="field_of_study" value={studyPrefs.field_of_study} onChange={handleStudyChange} disabled={ro} placeholder="Ex: Informatique, Médecine, Commerce..." required />
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Label>Université / Établissement préféré</Form.Label>
                    <Form.Control name="preferred_university" value={studyPrefs.preferred_university} onChange={handleStudyChange} disabled={ro} placeholder="Ex: Université de Montréal, McGill, UBC..." />
                </Form.Group>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ville préférée</Form.Label>
                            <Form.Select name="preferred_city" value={studyPrefs.preferred_city} onChange={handleStudyChange} disabled={ro}>
                                <option value="">Sélectionner</option>
                                {STUDY_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Date de début souhaitée</Form.Label>
                            <Form.Control type="date" name="start_date" value={studyPrefs.start_date} onChange={handleStudyChange} disabled={ro} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Durée (mois)</Form.Label>
                            <Form.Control type="number" name="duration_months" value={studyPrefs.duration_months} onChange={handleStudyChange} disabled={ro} min="1" placeholder="Ex: 12" />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Source de financement</Form.Label>
                            <Form.Control name="funding_source" value={studyPrefs.funding_source} onChange={handleStudyChange} disabled={ro} placeholder="Ex: Bourse, Fonds propres, Famille..." />
                        </Form.Group>
                    </Col>
                </Row>
                <Form.Group className="mb-3">
                    <Form.Check type="checkbox" name="has_acceptance_letter" checked={studyPrefs.has_acceptance_letter} onChange={handleStudyChange} disabled={ro}
                        label="J'ai déjà une lettre d'acceptation d'un établissement canadien" />
                </Form.Group>
            </>
        );
    };

    const renderDocumentsStep = () => (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-1">Documents du dossier</h5>
                    <p className="text-muted mb-0 small">Téléversez tous les documents obligatoires (*) pour pouvoir soumettre.</p>
                </div>
                {dossier.status === 'draft' && (
                    <Button variant="primary" size="sm" onClick={() => setShowUploadModal(true)}>
                        <FaFileUpload className="me-2" />Ajouter
                    </Button>
                )}
            </div>
            <div className="mb-4">
                <div className="d-flex justify-content-between mb-1">
                    <small className="text-muted">Documents obligatoires</small>
                    <small className="fw-bold">{REQUIRED_DOCS.length - missingRequired.length} / {REQUIRED_DOCS.length}</small>
                </div>
                <ProgressBar now={docCompletion} variant={docCompletion === 100 ? 'success' : 'primary'} style={{ height: 8, borderRadius: 4 }} />
            </div>
            <h6 className="text-muted mb-2 small text-uppercase">Obligatoires *</h6>
            <div className="mb-4">
                {REQUIRED_DOCS.map(doc => {
                    const uploaded = documents.find(d => d.doc_type === doc.value);
                    return (
                        <div key={doc.value} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                                {uploaded
                                    ? <FaCheck size={14} className="text-success flex-shrink-0" />
                                    : <FaClock size={14} className="text-secondary flex-shrink-0" />}
                                <span className={uploaded ? 'text-success' : 'text-body'}>{doc.label}</span>
                                {uploaded?.verified && <Badge bg="success" style={{ fontSize: '0.6rem' }}>Vérifié</Badge>}
                            </div>
                            <div className="d-flex gap-1">
                                {uploaded && (
                                    <Button size="sm" variant="outline-primary" title="Voir"
                                        onClick={() => window.open(`${api.defaults.baseURL}/documents/${uploaded.id}?token=${localStorage.getItem('access_token')}`, '_blank')}>
                                        <FaEye size={12} />
                                    </Button>
                                )}
                                {uploaded && dossier.status === 'draft' && (
                                    <Button size="sm" variant="outline-danger" title="Supprimer" onClick={() => handleDeleteDocument(uploaded.id)}>
                                        <FaTrash size={12} />
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {documents.filter(d => OPTIONAL_DOCS.some(od => od.value === d.doc_type)).length > 0 && (
                <>
                    <h6 className="text-muted mb-2 small text-uppercase">Optionnels</h6>
                    <div className="mb-3">
                        {documents.filter(d => OPTIONAL_DOCS.some(od => od.value === d.doc_type)).map(doc => {
                            const label = ALL_DOCS.find(d => d.value === doc.doc_type)?.label || doc.doc_type;
                            return (
                                <div key={doc.id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                                    <div className="d-flex align-items-center gap-2">
                                        <FaCheck size={14} className="text-success flex-shrink-0" />
                                        <span>{label}</span>
                                    </div>
                                    <div className="d-flex gap-1">
                                        <Button size="sm" variant="outline-primary"
                                            onClick={() => window.open(`${api.defaults.baseURL}/documents/${doc.id}?token=${localStorage.getItem('access_token')}`, '_blank')}>
                                            <FaEye size={12} />
                                        </Button>
                                        {dossier.status === 'draft' && (
                                            <Button size="sm" variant="outline-danger" onClick={() => handleDeleteDocument(doc.id)}>
                                                <FaTrash size={12} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            {missingRequired.length > 0 && (
                <Alert variant="warning" className="mb-0">
                    <FaExclamationTriangle className="me-2" />
                    Manquants : <strong>{missingRequired.map(d => d.label).join(', ')}</strong>
                </Alert>
            )}
        </>
    );

    const renderPaymentStep = () => (
        <>
            <h5 className="mb-1">Frais de dossier</h5>
            <p className="text-muted mb-4 small">Réglez les frais de traitement avant de soumettre votre dossier.</p>
            <Row className="mb-4 align-items-start">
                <Col md={8}>
                    <p className="text-muted mb-3">
                        Les frais d'étude du dossier s'élèvent à <strong>125 $ CAD</strong> et doivent être payés via notre partenaire sécurisé <strong>MoneyFusion</strong>.
                    </p>
                    <Alert variant="info" className="mb-3">
                        <strong>🔒 Paiement sécurisé</strong><br />
                        <small>MoneyFusion est conforme aux normes PCI DSS. Vos données bancaires sont chiffrées et entièrement protégées.</small>
                    </Alert>
                    <Button variant="primary" size="lg"
                        href="https://my.moneyfusion.net/69629bcd013a07719710b54d"
                        target="_blank" rel="noopener noreferrer" className="mb-2">
                        💳 Procéder au paiement sécurisé
                    </Button>
                    <p className="text-muted small">(Ouverture dans un nouvel onglet)</p>
                </Col>
                <Col md={4}>
                    <Card className={`text-center border-${dossier.payment_completed ? 'success' : 'warning'}`}>
                        <Card.Body className="py-4">
                            {dossier.payment_completed ? (
                                <>
                                    <FaCheck size={32} className="text-success mb-2" />
                                    <h5 className="text-success mb-0">Payé ✓</h5>
                                    <small className="text-muted">125 $ CAD</small>
                                </>
                            ) : (
                                <>
                                    <h3 className="text-warning mb-1">125 $</h3>
                                    <small className="text-muted">CAD — En attente</small>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <div className="border-top pt-4">
                <h6 className="mb-1">Téléverser le reçu de paiement</h6>
                <p className="text-muted small mb-3">Après paiement, téléversez votre reçu pour validation par notre équipe.</p>
                {dossier.payment_receipt && (
                    <Alert variant="success" className="mb-3">
                        <FaCheck className="me-2" />
                        <strong>Reçu déjà téléversé :</strong> {dossier.payment_receipt.split('/').pop()}
                        <Button variant="outline-success" size="sm" className="ms-2"
                            onClick={() => window.open(`${api.defaults.baseURL}/dossiers/${dossier.id}/payment-receipt?token=${localStorage.getItem('access_token')}`, '_blank')}>
                            <FaEye className="me-1" />Voir
                        </Button>
                    </Alert>
                )}
                <Form.Group className="mb-3">
                    <Form.Label>Fichier du reçu *</Form.Label>
                    <Form.Control type="file" onChange={e => setPaymentReceipt(e.target.files[0])}
                        accept=".pdf,.jpg,.jpeg,.png" disabled={dossier.payment_completed} />
                    <Form.Text className="text-muted">PDF, JPG, PNG — Max 10 MB</Form.Text>
                </Form.Group>
                {paymentReceipt && <Alert variant="info" className="mb-3"><strong>Sélectionné :</strong> {paymentReceipt.name}</Alert>}
                <Button variant="success" onClick={handlePaymentReceiptUpload}
                    disabled={!paymentReceipt || uploadingPayment || dossier.payment_completed}>
                    {uploadingPayment
                        ? <><span className="spinner-border spinner-border-sm me-2" />Téléversement...</>
                        : <><FaFileUpload className="me-2" />Téléverser le reçu</>}
                </Button>
            </div>
        </>
    );

    const renderReviewStep = () => {
        const canSubmit = missingRequired.length === 0;
        const prefsStepIdx = hasPrefsStep ? 0 : -1;
        const docsStepIdx  = hasPrefsStep ? 1 : 0;
        const payStepIdx   = hasPrefsStep ? 2 : 1;
        return (
            <>
                <h5 className="mb-1">Révision et soumission</h5>
                <p className="text-muted small mb-4">Vérifiez que tout est complet avant de soumettre votre dossier.</p>
                <div className="mb-4 d-flex flex-column gap-2">
                    {hasPrefsStep && (
                        <div className={`d-flex align-items-center gap-3 p-3 rounded border ${prefsExist ? 'border-success bg-success bg-opacity-10' : 'border-warning bg-warning bg-opacity-10'}`}>
                            {prefsExist ? <FaCheck className="text-success flex-shrink-0" /> : <FaExclamationTriangle className="text-warning flex-shrink-0" />}
                            <div className="flex-grow-1">
                                <strong>Préférences</strong>
                                <div className="small text-muted">{prefsExist ? 'Renseignées ✓' : 'Non renseignées'}</div>
                            </div>
                            {!prefsExist && <Button variant="outline-warning" size="sm" onClick={() => setCurrentStep(prefsStepIdx)}>Renseigner</Button>}
                        </div>
                    )}
                    <div className={`d-flex align-items-center gap-3 p-3 rounded border ${canSubmit ? 'border-success bg-success bg-opacity-10' : 'border-warning bg-warning bg-opacity-10'}`}>
                        {canSubmit ? <FaCheck className="text-success flex-shrink-0" /> : <FaExclamationTriangle className="text-warning flex-shrink-0" />}
                        <div className="flex-grow-1">
                            <strong>Documents obligatoires</strong>
                            <div className="small text-muted">
                                {canSubmit
                                    ? `${REQUIRED_DOCS.length} / ${REQUIRED_DOCS.length} fournis ✓`
                                    : `${REQUIRED_DOCS.length - missingRequired.length} / ${REQUIRED_DOCS.length} — Manquants : ${missingRequired.map(d => d.label).join(', ')}`}
                            </div>
                        </div>
                        {!canSubmit && <Button variant="outline-warning" size="sm" onClick={() => setCurrentStep(docsStepIdx)}>Compléter</Button>}
                    </div>
                    <div className={`d-flex align-items-center gap-3 p-3 rounded border ${dossier.payment_receipt ? 'border-success bg-success bg-opacity-10' : 'border-light bg-light'}`}>
                        {dossier.payment_receipt ? <FaCheck className="text-success flex-shrink-0" /> : <FaClock className="text-muted flex-shrink-0" />}
                        <div className="flex-grow-1">
                            <strong>Reçu de paiement</strong>
                            <div className="small text-muted">{dossier.payment_receipt ? 'Téléversé ✓' : 'Non obligatoire pour la soumission'}</div>
                        </div>
                        {!dossier.payment_receipt && <Button variant="outline-secondary" size="sm" onClick={() => setCurrentStep(payStepIdx)}>Uploader</Button>}
                    </div>
                </div>

                {dossier.admin_comment && (
                    <Alert variant={dossier.status === 'rejected' ? 'danger' : 'info'} className="mb-4">
                        <strong>{dossier.status === 'rejected' ? 'Motif de rejet' : 'Commentaire'} :</strong> {dossier.admin_comment}
                    </Alert>
                )}

                {canSubmit ? (
                    <Alert variant="success" className="mb-4">
                        <FaCheck className="me-2" />
                        <strong>Dossier prêt !</strong> Vous pouvez soumettre votre candidature. Après soumission, vous ne pourrez plus modifier votre dossier.
                    </Alert>
                ) : (
                    <Alert variant="warning" className="mb-4">
                        <FaExclamationTriangle className="me-2" />
                        Complétez les éléments manquants avant de soumettre.
                    </Alert>
                )}

                <div className="d-flex gap-3 flex-wrap">
                    <Button variant="success" size="lg" onClick={handleSubmitDossier} disabled={!canSubmit}>
                        <FaCheck className="me-2" />Soumettre mon dossier
                    </Button>
                    <Button variant="outline-danger" onClick={handleDeleteDossier}>
                        <FaTrash className="me-2" />Supprimer ce dossier
                    </Button>
                </div>
            </>
        );
    };

    const renderCurrentStep = () => {
        switch (STEPS[currentStep]?.id) {
            case 'preferences': return renderPreferencesStep();
            case 'documents':   return renderDocumentsStep();
            case 'payment':     return renderPaymentStep();
            case 'review':      return renderReviewStep();
            default: return null;
        }
    };

    // ── Submitted / read-only view ─────────────────────────────────────────────
    const renderStatusView = () => (
        <>
            <Alert variant={dossier.status === 'approved' ? 'success' : dossier.status === 'rejected' ? 'danger' : 'info'} className="mb-4">
                <h5 className="mb-1">
                    {dossier.status === 'approved'  && <><FaCheck className="me-2" />Dossier approuvé</>}
                    {dossier.status === 'rejected'  && <><FaTimes className="me-2" />Dossier rejeté</>}
                    {dossier.status === 'submitted' && <><FaClock className="me-2" />Dossier soumis — en attente de traitement</>}
                    {dossier.status === 'in_review' && <><FaClock className="me-2" />Dossier en cours d'examen</>}
                </h5>
                {dossier.admin_comment && <p className="mb-0 mt-1"><strong>Note :</strong> {dossier.admin_comment}</p>}
            </Alert>
            <Card className="mb-4 shadow-sm">
                <Card.Header className="d-flex align-items-center gap-2">
                    <FaFileAlt /><strong>Documents soumis ({documents.length})</strong>
                </Card.Header>
                <Card.Body>
                    {documents.length > 0 ? (
                        <Table responsive size="sm" hover>
                            <thead><tr><th>Document</th><th>Date</th><th>Statut</th><th></th></tr></thead>
                            <tbody>
                                {documents.map(doc => (
                                    <tr key={doc.id}>
                                        <td>{ALL_DOCS.find(d => d.value === doc.doc_type)?.label || doc.doc_type}</td>
                                        <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                                        <td>{doc.verified ? <Badge bg="success">Vérifié</Badge> : <Badge bg="secondary">En attente</Badge>}</td>
                                        <td>
                                            <Button size="sm" variant="outline-primary"
                                                onClick={() => window.open(`${api.defaults.baseURL}/documents/${doc.id}?token=${localStorage.getItem('access_token')}`, '_blank')}>
                                                <FaEye size={12} className="me-1" />Voir
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : <p className="text-muted mb-0">Aucun document.</p>}
                </Card.Body>
            </Card>
        </>
    );

    // ── Render ─────────────────────────────────────────────────────────────────
    if (loading) return (
        <Container className="py-5 text-center">
            <div className="spinner-border text-primary" />
            <p className="mt-3 text-muted">Chargement...</p>
        </Container>
    );

    if (!dossier) return (
        <Container className="py-5"><Alert variant="danger">Dossier introuvable.</Alert></Container>
    );

    const statusConf = STATUS_CONFIG[dossier.status] || { label: dossier.status, variant: 'secondary' };

    return (
        <Container className="py-4 pb-5" style={{ maxWidth: 1400 }}>
            {/* Header */}
            <div className="d-flex align-items-start justify-content-between mb-4">
                <div>
                    <h2 className="mb-1">Dossier #{dossier.id}</h2>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                        <Badge bg={statusConf.variant}>{statusConf.label}</Badge>
                        <Badge bg="primary">{VISA_LABELS[dossier.visa_type] || dossier.visa_type}</Badge>
                        <small className="text-muted">Créé le {new Date(dossier.created_at).toLocaleDateString()}</small>
                    </div>
                </div>
                <Button variant="outline-secondary" size="sm" onClick={() => navigate('/mes-dossiers')}>
                    ← Mes dossiers
                </Button>
            </div>

            {/* Draft: stepper */}
            {dossier.status === 'draft' && STEPS.length > 0 && (
                <>
                    <StepIndicator steps={STEPS} currentStep={currentStep} />
                    <div className="mb-3">
                        <small className="text-muted">Étape {currentStep + 1} sur {STEPS.length}</small>
                        <h4 className="mb-0 text-primary fw-bold">{STEPS[currentStep]?.title}</h4>
                    </div>
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body className="p-4">{renderCurrentStep()}</Card.Body>
                    </Card>
                    <div className="d-flex justify-content-center align-items-center gap-4">
                        <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 0} style={{ minWidth: 130 }}>
                            <FaChevronLeft className="me-2" size={12} />Précédent
                        </Button>
                        <span className="text-muted small">{currentStep + 1} / {STEPS.length}</span>
                        {currentStep < STEPS.length - 1 ? (
                            <Button variant="primary" onClick={handleNext} disabled={saving} style={{ minWidth: 130 }}>
                                {saving
                                    ? <><span className="spinner-border spinner-border-sm me-2" />Sauvegarde...</>
                                    : <>Suivant<FaChevronRight className="ms-2" size={12} /></>}
                            </Button>
                        ) : (
                            <div style={{ minWidth: 130 }} />
                        )}
                    </div>
                </>
            )}

            {/* Submitted/reviewed: read-only */}
            {dossier.status !== 'draft' && renderStatusView()}

            {/* Upload Modal */}
            <Modal show={showUploadModal} onHide={() => { setShowUploadModal(false); setUploadFile(null); setDocType(''); }}>
                <Modal.Header closeButton>
                    <Modal.Title><FaFileUpload className="me-2" />Ajouter un document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Type de document *</Form.Label>
                        <Form.Select value={docType} onChange={e => setDocType(e.target.value)} required>
                            <option value="">Sélectionner un type</option>
                            <optgroup label="Obligatoires">
                                {REQUIRED_DOCS.map(d => <option key={d.value} value={d.value}>{d.label} *</option>)}
                            </optgroup>
                            <optgroup label="Optionnels">
                                {OPTIONAL_DOCS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </optgroup>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Fichier *</Form.Label>
                        <Form.Control type="file" onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" />
                        <Form.Text className="text-muted">PDF, JPG, PNG — Max 10 MB</Form.Text>
                    </Form.Group>
                    {uploadFile && <Alert variant="info" className="mt-2 mb-0"><strong>Sélectionné :</strong> {uploadFile.name}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowUploadModal(false); setUploadFile(null); setDocType(''); }}>Annuler</Button>
                    <Button variant="primary" onClick={handleFileUpload} disabled={uploading || !uploadFile || !docType}>
                        {uploading
                            ? <><span className="spinner-border spinner-border-sm me-2" />Téléversement...</>
                            : <><FaFileUpload className="me-2" />Téléverser</>}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default DossierDetails;
